import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Plus, Trash2, ExternalLink, Heart, LayoutGrid, User as UserIcon, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { formatPrice, catMeta, imgUrl, PLACEHOLDER_IMAGE } from "../lib/format";
import ListingCard from "../components/ListingCard";

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "listings";

  const [myListings, setMyListings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "", whatsapp: user?.whatsapp || "" });
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    api.get("/my/listings").then((r) => setMyListings(r.data)).catch(() => {});
    api.get("/favorites").then((r) => { setFavorites(r.data); setFavIds(r.data.map((l) => l.id)); }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteListing = async (id) => {
    await api.delete(`/listings/${id}`);
    load();
  };

  const renewListing = async (id) => {
    await api.post(`/listings/${id}/renew`);
    navigate(`/pay/${id}`);
  };

  const toggleFav = async (id) => {
    const res = await api.post(`/favorites/${id}`);
    if (!res.data.favorited) {
      setFavorites((p) => p.filter((l) => l.id !== id));
      setFavIds((p) => p.filter((x) => x !== id));
    }
  };

  const saveProfile = async () => {
    const res = await api.put("/auth/profile", profile);
    setUser(res.data);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const setTab = (tb) => setParams({ tab: tb });

  const tabs = [
    { key: "listings", label: t("my_listings"), icon: LayoutGrid },
    { key: "favorites", label: t("saved"), icon: Heart },
    { key: "profile", label: t("profile_settings"), icon: UserIcon },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-24 pb-16">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-white/40 text-sm">{t("welcome")},</p>
          <h1 className="font-display text-3xl md:text-4xl text-white">{user?.name}</h1>
        </div>
        <Link to="/post" className="bg-gold text-black px-5 py-2.5 font-medium hover:bg-gold-light transition-colors inline-flex items-center gap-2" data-testid="dash-post-btn">
          <Plus size={18} /> {t("nav_post")}
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto">
        {tabs.map((tb) => {
          const Icon = tb.icon;
          const active = tab === tb.key;
          return (
            <button key={tb.key} data-testid={`tab-${tb.key}`} onClick={() => setTab(tb.key)} className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${active ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"}`}>
              <Icon size={16} /> {tb.label}
            </button>
          );
        })}
      </div>

      {/* My Listings */}
      {tab === "listings" && (
        <div data-testid="my-listings-tab">
          {myListings.length === 0 ? (
            <Empty text={lang === "en" ? "You haven't posted any listings yet." : "Vous n'avez pas encore publié d'annonces."} cta={t("nav_post")} onCta={() => navigate("/post")} />
          ) : (
            <div className="space-y-4">
              {myListings.map((l) => <MyListingRow key={l.id} listing={l} lang={lang} t={t} onDelete={deleteListing} onRenew={renewListing} navigate={navigate} />)}
            </div>
          )}
        </div>
      )}

      {/* Favorites */}
      {tab === "favorites" && (
        <div data-testid="favorites-tab">
          {favorites.length === 0 ? (
            <Empty text={lang === "en" ? "No saved favorites yet." : "Aucun favori enregistré."} cta={t("nav_browse")} onCta={() => navigate("/browse")} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {favorites.map((l, i) => <ListingCard key={l.id} listing={l} index={i} favorited={favIds.includes(l.id)} onToggleFav={toggleFav} />)}
            </div>
          )}
        </div>
      )}

      {/* Profile */}
      {tab === "profile" && (
        <div className="max-w-lg" data-testid="profile-tab">
          <div className="bg-[#121212] border border-white/10 p-8 space-y-6">
            <div className="flex items-center gap-4">
              {user?.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-white/10" />}
              <div>
                <p className="text-white font-medium">{user?.email}</p>
                {user?.is_admin && <span className="text-gold text-xs">Admin</span>}
              </div>
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">{t("f_contact_name")}</label>
              <input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">{t("f_phone")}</label>
              <input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} className={inputCls} placeholder="+237 6XX XX XX XX" />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">{t("f_whatsapp")}</label>
              <input data-testid="profile-whatsapp" value={profile.whatsapp} onChange={(e) => setProfile((p) => ({ ...p, whatsapp: e.target.value }))} className={inputCls} placeholder="+237 6XX XX XX XX" />
            </div>
            <button data-testid="save-profile-btn" onClick={saveProfile} className="bg-gold text-black px-6 py-3 font-medium hover:bg-gold-light transition-colors">
              {saved ? (lang === "en" ? "Saved ✓" : "Enregistré ✓") : t("edit_profile")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-white/15 px-4 py-3 text-white outline-none focus:border-gold transition-colors placeholder-white/30";

const STATUS_META = {
  active: { icon: CheckCircle2, color: "text-green-400", bg: "bg-green-500/10 border-green-500/30" },
  pending: { icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30" },
  awaiting_review: { icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30" },
  rejected: { icon: AlertCircle, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
  expired: { icon: AlertCircle, color: "text-white/50", bg: "bg-white/5 border-white/15" },
};

function statusLabel(status, lang) {
  const map = {
    active: { en: "Active", fr: "Active" },
    pending: { en: "Awaiting Payment", fr: "Paiement en attente" },
    awaiting_review: { en: "Under Review", fr: "En révision" },
    rejected: { en: "Rejected", fr: "Rejetée" },
    expired: { en: "Expired", fr: "Expirée" },
  };
  return (map[status] || map.pending)[lang];
}

function daysLeft(expires_at) {
  if (!expires_at) return null;
  const ms = new Date(expires_at).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

function MyListingRow({ listing, lang, t, onDelete, onRenew, navigate }) {
  const meta = catMeta(listing.category);
  const sm = STATUS_META[listing.status] || STATUS_META.pending;
  const SIcon = sm.icon;
  const img = imgUrl(listing.images?.[0]) || PLACEHOLDER_IMAGE;
  const dleft = daysLeft(listing.expires_at);
  const expSoon = listing.status === "active" && dleft != null && dleft <= 7;
  return (
    <div className="flex items-center gap-4 bg-[#121212] border border-white/10 p-3" data-testid={`my-listing-${listing.id}`}>
      <img src={img} alt="" className="w-24 h-20 object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium line-clamp-1">{listing.title}</p>
        <p className="text-white/40 text-sm">{listing.city} · {meta[lang]}</p>
        <p className="text-gold font-display mt-1">{formatPrice(listing.price)}</p>
        {listing.status === "active" && listing.expires_at && (
          <p className={`text-xs mt-1 ${expSoon ? "text-yellow-400" : "text-white/40"}`} data-testid={`expiry-${listing.id}`}>
            {t("expires")} {new Date(listing.expires_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            {dleft != null && dleft >= 0 ? ` · ${dleft} ${t("days_left")}` : ""}
          </p>
        )}
      </div>
      <div className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 border text-xs ${sm.bg} ${sm.color}`}>
        <SIcon size={14} /> {statusLabel(listing.status, lang)}
      </div>
      <div className="flex items-center gap-2">
        {listing.status === "pending" && (
          <button data-testid={`pay-listing-${listing.id}`} onClick={() => navigate(`/pay/${listing.id}`)} className="text-xs bg-gold text-black px-3 py-2 hover:bg-gold-light transition-colors">{t("complete_payment")}</button>
        )}
        {(listing.status === "expired" || expSoon) && (
          <button data-testid={`renew-listing-${listing.id}`} onClick={() => onRenew(listing.id)} className="text-xs bg-gold text-black px-3 py-2 hover:bg-gold-light transition-colors">{t("renew")}</button>
        )}
        {listing.status === "active" && (
          <Link to={`/listing/${listing.id}`} className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-gold transition-colors"><ExternalLink size={15} className="text-white/70" /></Link>
        )}
        <button data-testid={`delete-listing-${listing.id}`} onClick={() => onDelete(listing.id)} className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-red-500 transition-colors"><Trash2 size={15} className="text-white/70" /></button>
      </div>
    </div>
  );
}

function Empty({ text, cta, onCta }) {
  return (
    <div className="text-center py-24 border border-white/10 bg-[#121212]">
      <p className="text-white/50 mb-5">{text}</p>
      <button onClick={onCta} className="bg-gold text-black px-6 py-2.5 font-medium hover:bg-gold-light transition-colors">{cta}</button>
    </div>
  );
}
